function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import PubSub from 'pubsub-js';
import { Link } from "react-router-dom";
import { withRouter } from "react-router";
import { PageHeader, Ribbon, Card, Pending, HBars, Empty } from "../../components/analytics-kit";
import Error from "../../components/error";
import SetupStrip from "../onboarding/setup-strip";
import ChartBoundary from "../market-intelligence/chart-boundary";
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartValueAxis, ChartValueAxisItem, ChartTooltip, ChartLegend } from "@progress/kendo-react-charts";
import ServiceFactory from "../../service";
import publishSearch from "../../functions/publish-search";
import publishEvent from "../../functions/publish-event";
import compLabel from "../../functions/comp-label";
import processProductUrls from "../../functions/process-product-urls";
import { hasTenantFeature } from "../../functions/tenant-features";
import Auth from "../../functions/authenticate";
import TenantModules from "../../components/tenant-modules";
import { workspaceContext, available, sizeOf, plunkCard, plunkDefault, entryOf, sameLayout, MAX_CARDS, getMyLayout, saveMyLayout, resetMyLayout, seenDefault, getDefaultLayout, saveDefaultLayout, clearDefaultLayout, reportCard, runReportPanel, reportComparison, cachedLayout, rememberLayout } from "./layout";
import { ReportPanel, periodOf, cleanTitle } from "../reports/panel";
import { repSvc } from "../reports/common";
import { EditBar, CardTools, AddCardSheet, DefaultConfirm, Toast } from "./edit";
import { RANGES, num, usd, fetchSales, deltaOf, deltaOfPaced, comparisonQs, trendLabel, resolveBusinessModel, rangeLabelFor } from "../sales/territory-common";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { previousPreset, dailyRows } from "../web-activity/data";

/*
 * Home dashboard on the App UI standard kit (PlunkDocs specs/APP_UI_STANDARD_2026-09-29.md).
 *
 * The page is the tenant's morning briefing: what changed → money → site activity →
 * funnel + revenue trend → demand signals → products. Every block renders as soon as
 * its own data lands (no all-or-nothing wait: the known-contacts call alone can run to
 * the gateway timeout). Honesty rules from CONTACT_UX_REDESIGN.md §1, DO NOT re-add:
 *   - Interaction Value / Conversion Value tiles (configurator price sums and valueless
 *     dealer applications are not revenue). The ONLY dollars on this page are real
 *     sales transactions.
 *   - The 3-series single-axis chart (conversions flatlined under the impressions
 *     axis). Small multiples / sparklines, one scale each.
 *   - The 2-slice pies. Named bar lists (dealer buying signals as counts).
 *
 * Data: /v1/activity/dashboard/{range} (+ the previous preset for activity deltas) ·
 * /v1/activity/contacts/dashboard/{range} (known-contacts slice) · /v1/lifecycle/funnel/
 * {range} (lifecycle-gated) · /v1/sales/totals + /v1/sales/trend (salesTransactions-
 * gated) · /metrics/product (top views).
 *
 * My Dashboard (PlunkDocs specs/MY_DASHBOARD_2026-10-22.md, step 1): the blocks are catalog cards (./layout.js) drawn
 * in the person's layout (theirs → workspace default → Plunk's default). Only the data the shown cards need is fetched.
 * Customize = edit mode (./edit.jsx): reorder (drag or Move up/down), size, remove, Add card, reset, and for owners and
 * administrators "Save as the workspace default".
 */

function activitySvc(api) {
  const Service = ServiceFactory.getType(api); // 'activity' root
  return new Service();
}
async function safe(promise) {
  try {
    return await promise;
  } catch (e) {
    return null;
  }
}

// [[header],[type,hits]...] → sorted {name, value} list.
function toBarList(pairs, top) {
  return (pairs || []).slice(1).map(([label, value]) => ({
    name: label,
    value: Number(value) || 0
  })).filter(r => r.name).sort((a, b) => b.value - a.value).slice(0, top);
}
const kitDelta = d => d && d.pct != null ? {
  dir: d.dir,
  pct: d.pct
} : null;
const usdShort = n => {
  n = Number(n) || 0;
  const a = Math.abs(n);
  if (!a) return "$0";
  return a >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : a >= 1e3 ? `$${(n / 1e3).toFixed(0)}K` : usd(n);
};
const AXIS_FONT = "11px Proxima Nova, sans-serif";

// The data each card draws on ("dash", the activity payload, is always fetched: the tracker chip reads it).
/** The API's plain error text, from a service rejection (the body arrives as JSON in e.message). */
const errOf = (e, fallback) => {
  const m = e && e.message;
  try {
    const b = JSON.parse(m);
    if (b && b.error) return b.error;
  } catch (x) {/* not JSON */}
  // The service layer passes the body's `message` through; a bare status text ("Bad Request") is no help to anyone.
  return typeof m === "string" && /\s/.test(m) && m.length > 20 && m.length < 300 ? m : fallback;
};
const NEEDS = {
  changed: ["salesNow", "funnel", "products"],
  sales: ["salesNow", "salesYtd", "trend"],
  activity: ["prev", "contacts"],
  funnel: ["funnel"],
  trend: ["trend"],
  conversions: [],
  interactions: [],
  products: ["products"],
  modules: []
};
const FUNNEL_META = [["lead", "Leads"], ["prospect", "Prospects"], ["customer", "New customers"]];
class DashboardComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "removeNow", async id => {
      const cards = (this.state.layout && this.state.layout.cards || []).filter(c => c.id !== id);
      try {
        await saveMyLayout(cards);
        this.setState({
          layout: {
            ...this.state.layout,
            source: "mine",
            cards
          }
        });
      } catch (e) {
        this.setState({
          toast: {
            text: "Could not remove the card. Try again."
          }
        });
      }
    });
    _defineProperty(this, "goto", path => () => this.props.history.push(path));
    _defineProperty(this, "dismissNudge", () => {
      try {
        localStorage.setItem("plunk-askplunk-nudge-dismissed", "1");
      } catch (e) {/* private mode */}
      this.setState({
        askPlunkNudge: false
      });
    });
    _defineProperty(this, "customize", () => this.setState({
      draft: this.cards().map(c => ({
        ...c
      })),
      toast: null
    }));
    _defineProperty(this, "cancel", () => this.setState({
      draft: null,
      adding: false,
      dragFrom: null
    }));
    /** "New chart": keep what was arranged so far, then the builder's one-chart mode (it saves, pins and comes back). */
    _defineProperty(this, "newChart", async () => {
      const {
        draft,
        layout
      } = this.state;
      if (draft && !sameLayout(draft, layout.cards)) {
        this.setState({
          saving: true
        });
        try {
          await saveMyLayout(draft);
        } catch (e) {
          this.setState({
            saving: false,
            toast: {
              text: errOf(e, "Your Dashboard could not be saved. Try again.")
            }
          });
          return;
        }
      }
      this.props.history.push("/Reports/new?chart=1");
    });
    _defineProperty(this, "addReport", (report, panel) => {
      const cards = this.cards();
      if (cards.length >= MAX_CARDS) return;
      this.setDraft([...cards, reportCard(report.id, panel.id, panel.width)]);
    });
    _defineProperty(this, "add", key => {
      const cards = this.cards();
      if (cards.length >= MAX_CARDS) return;
      const e = entryOf(key);
      const size = e.sizes.includes(6) ? 6 : e.sizes[e.sizes.length - 1];
      this.setDraft([...cards.filter(c => c.id !== `p-${key}`), plunkCard(key, size)]);
    });
    _defineProperty(this, "done", async () => {
      const {
        draft,
        layout
      } = this.state;
      if (!draft || sameLayout(draft, layout.cards)) {
        this.cancel();
        return;
      }
      this.setState({
        saving: true
      });
      try {
        await saveMyLayout(draft);
        this.setState({
          layout: {
            ...layout,
            source: "mine",
            cards: draft,
            defaultChanged: false
          },
          draft: null,
          saving: false,
          adding: false
        });
      } catch (e) {
        this.setState({
          saving: false,
          toast: {
            text: errOf(e, "Your Dashboard could not be saved. Try again.")
          }
        });
      }
    });
    _defineProperty(this, "reset", async () => {
      this.setState({
        saving: true
      });
      try {
        const r = await resetMyLayout();
        const removed = r && r.removed || null;
        await this.loadLayout();
        const where = this.state.layout.source === "default" ? "the workspace default" : "Plunk's default";
        this.setState({
          draft: null,
          saving: false,
          toast: {
            text: `Your Dashboard is back to ${where}.`,
            action: removed ? "Undo" : null,
            undo: removed
          }
        }, () => this.fillMissing());
      } catch (e) {
        this.setState({
          saving: false,
          toast: {
            text: "Could not reset your Dashboard. Try again."
          }
        });
      }
    });
    _defineProperty(this, "undoReset", async () => {
      const cards = this.state.toast && this.state.toast.undo;
      this.setState({
        toast: null
      });
      if (!cards) return;
      try {
        await saveMyLayout(cards);
        await this.loadLayout();
        this.fillMissing();
      } catch (e) {
        this.setState({
          toast: {
            text: "Could not restore your layout."
          }
        });
      }
    });
    _defineProperty(this, "askDefault", async () => {
      let customized = 0;
      try {
        const d = await getDefaultLayout();
        customized = Number(d && d.customized) || 0;
      } catch (e) {/* show the dialog anyway */}
      // The person saving it doesn't count: they're the one changing it.
      if (this.state.layout && this.state.layout.source === "mine") customized = Math.max(0, customized - 1);
      this.setState({
        askDefault: {
          customized
        }
      });
    });
    _defineProperty(this, "saveDefault", async () => {
      const {
        draft,
        layout
      } = this.state;
      this.setState({
        saving: true
      });
      try {
        await saveDefaultLayout(draft);
        // An admin with their own layout keeps seeing what they just arranged.
        if (layout.source === "mine") await saveMyLayout(draft);
        this.setState({
          layout: {
            ...layout,
            cards: draft,
            source: layout.source === "mine" ? "mine" : "default",
            hasDefault: true,
            defaultChanged: false
          },
          draft: null,
          saving: false,
          askDefault: null,
          toast: {
            text: "Saved as the workspace default."
          }
        });
      } catch (e) {
        this.setState({
          saving: false,
          askDefault: null,
          toast: {
            text: errOf(e, "Could not save the workspace default.")
          }
        });
      }
    });
    /** Owners and administrators: back to Plunk's default for everyone without their own layout. Undo puts it back. */
    _defineProperty(this, "clearDefault", async () => {
      this.setState({
        saving: true
      });
      try {
        const before = await getDefaultLayout();
        await clearDefaultLayout();
        await this.loadLayout();
        this.setState({
          draft: null,
          saving: false,
          toast: {
            text: "Removed the workspace default. People without their own layout see Plunk's default.",
            action: before && before.cards ? "Undo" : null,
            restoreDefault: before && before.cards
          }
        }, () => this.fillMissing());
      } catch (e) {
        this.setState({
          saving: false,
          toast: {
            text: errOf(e, "Could not remove the workspace default.")
          }
        });
      }
    });
    _defineProperty(this, "toastAction", async () => {
      const t = this.state.toast || {};
      if (t.restoreDefault) {
        this.setState({
          toast: null
        });
        try {
          await saveDefaultLayout(t.restoreDefault);
          await this.loadLayout();
          this.fillMissing();
        } catch (e) {
          this.setState({
            toast: {
              text: "Could not restore the workspace default."
            }
          });
        }
        return;
      }
      this.undoReset();
    });
    _defineProperty(this, "useNewDefault", async () => {
      try {
        await resetMyLayout();
      } catch (e) {/* reload shows what's there */}
      await this.loadLayout();
      this.fillMissing();
    });
    _defineProperty(this, "keepMine", async () => {
      this.set({
        layout: {
          ...this.state.layout,
          defaultChanged: false
        }
      });
      try {
        await seenDefault();
      } catch (e) {/* note returns next time */}
    });
    let askPlunkNudge = false;
    try {
      askPlunkNudge = !localStorage.getItem("plunk-askplunk-nudge-dismissed");
    } catch (e) {/* private mode */}
    this.state = {
      ready: false,
      error: false,
      comp: getGlobalComp("prev_period"),
      askPlunkNudge,
      businessModel: "manufacturing",
      // undefined = loading · null = unavailable
      dash: undefined,
      prev: undefined,
      contacts: undefined,
      funnel: undefined,
      salesNow: undefined,
      salesYtd: undefined,
      trend: undefined,
      products: undefined,
      // layout: { source, cards, defaultChanged, defaultBy, hasDefault, canSetDefault } · draft = cards while customizing
      layout: null,
      draft: null,
      saving: false,
      adding: false,
      askDefault: null,
      toast: null,
      dragFrom: null,
      // report charts (step 2): card id → { status: loading|ok|error, run, code, error } · registry for metric labels
      reports: {},
      registry: undefined
    };
    this._alive = true;
  }
  range() {
    return getGlobalRange("this_month");
  }

  // ------------------------------------------------------------- layout
  ctx() {
    return this._ctx || (this._ctx = workspaceContext());
  }
  async loadLayout() {
    let layout;
    try {
      layout = await getMyLayout();
    } catch (e) {
      layout = {
        source: "plunk",
        cards: plunkDefault(this.ctx()),
        failed: true
      };
    }
    this.set({
      layout
    });
    return layout;
  }
  cards() {
    return this.state.draft || this.state.layout && this.state.layout.cards || [];
  }
  shown() {
    const ctx = this.ctx();
    return this.cards().filter(c => available(c, ctx));
  }
  needs() {
    const need = new Set();
    this.shown().forEach(c => (NEEDS[c.key] || []).forEach(n => need.add(n)));
    return need;
  }
  set(patch) {
    if (this._alive) this.setState(patch);
  }
  async componentDidMount() {
    publishSearch("");
    publishEvent("reset-search", {});
    // Auth guard: signed out → silent SSO attach on *.plunk.io, else the sign-in page.
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) {
      // Draw at once from the last layout this browser saw (else Plunk's default) and start fetching; the server's
      // layout replaces it when it lands and only what it adds is fetched then. Customize waits for the real one.
      // (load() runs from the setState callback: when the session is already signed in nothing above awaited, so
      // React batches this update and a plain load() right after it would still see no layout.)
      const guess = cachedLayout();
      const provisional = {
        source: guess && guess.source || "plunk",
        cards: guess && guess.cards || plunkDefault(this.ctx()),
        provisional: true
      };
      await new Promise(resolve => this.setState({
        ready: true,
        layout: provisional
      }, () => {
        this.load();
        resolve();
      }));
      await this.loadLayout();
      this.fillMissing();
      // Coming back from "New chart": the builder leaves its confirmation here.
      try {
        const t = sessionStorage.getItem("plunk-dash-toast");
        if (t) {
          sessionStorage.removeItem("plunk-dash-toast");
          this.set({
            toast: {
              text: t
            }
          });
        }
      } catch (e) {/* private mode */}
      resolveBusinessModel().then(bm => this.set({
        businessModel: bm
      })).catch(() => {/* manufacturer default */});
    }
    // Global period control (top bar): reload when the range/comparison changes.
    this._periodTok = subscribePeriod(({
      comp
    }) => {
      if (!this._alive) return;
      this.setState({
        comp: comp || this.state.comp,
        error: false
      }, () => this.load());
    });
  }
  componentDidUpdate(prevProps, prevState) {
    // Every real layout change (load, Done, reset, Remove, new default) refreshes the browser's last-seen copy.
    const l = this.state.layout;
    if (l !== prevState.layout && l && !l.provisional && !l.failed) rememberLayout(l);
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }

  /** Fetch what the shown cards need. `only` = just these groups (a card added while customizing). */
  load(only) {
    const range = this.range();
    const comp = this.state.comp;
    const hasSales = hasTenantFeature("salesTransactions");
    const hasLifecycle = hasTenantFeature("lifecycle");
    const need = this.needs();
    const want = g => need.has(g) && (!only || only.includes(g));
    const gen = only ? this._gen : this._gen = (this._gen || 0) + 1;
    const put = patch => {
      if (gen === this._gen) this.set(patch);
    };
    const reset = {};
    const mark = (g, ok) => {
      if (!only || only.includes(g)) reset[g] = want(g) && ok ? undefined : only ? this.state[g] : null;
    };
    mark("prev", true);
    mark("contacts", true);
    mark("funnel", hasLifecycle);
    mark("salesNow", hasSales);
    mark("salesYtd", hasSales);
    mark("trend", hasSales);
    mark("products", true);
    if (!only) reset.dash = undefined;
    this.set(reset);
    this._loaded = new Set([...(only ? this._loaded || [] : []), ...[...need].filter(g => !only || only.includes(g))]);

    // The activity payload is the one required call; everything else fills its own block.
    if (!only) activitySvc("/dashboard").getAsync({
      id: range
    }).then(d => put({
      dash: d || null,
      error: !d
    })).catch(() => put({
      dash: null,
      error: true
    }));
    // Activity deltas: the previous preset (this_month → last_month). Previous-year has no
    // activity preset, so the ribbon shows "no comparison" there.
    if (want("prev")) {
      const prevRange = comp === "prev_period" ? previousPreset(range) : null;
      if (prevRange) safe(activitySvc("/dashboard").getAsync({
        id: prevRange
      })).then(d => put({
        prev: d || null
      }));else put({
        prev: null
      });
    }
    if (want("contacts")) safe(activitySvc(`/contacts/dashboard/${range}${comparisonQs(range, true, comp)}`).getAsync()).then(c => put({
      contacts: c || null
    }));
    if (hasLifecycle && want("funnel")) safe(fetchSales(`/lifecycle/funnel/${range}${comparisonQs(range, true, comp)}`)).then(f => put({
      funnel: Array.isArray(f) ? f : null
    }));
    if (hasSales) {
      if (want("salesNow")) safe(fetchSales(`/sales/totals/${range}${comparisonQs(range, true, comp)}`)).then(s => put({
        salesNow: s || null
      }));
      if (want("salesYtd")) safe(fetchSales("/sales/totals/this_year?comparison=previous_year")).then(s => put({
        salesYtd: s || null
      }));
      if (want("trend")) Promise.all([safe(fetchSales("/sales/trend/last_year?bucket=month")), safe(fetchSales("/sales/trend/this_year?bucket=month"))]).then(([last, cur]) => put({
        trend: [...(last || []), ...(cur || [])].slice(-12)
      })); // trailing 12 months
    }
    if (want("products")) safe(this.fetchTopProducts(range)).then(p => put({
      products: p || null
    }));
    if (!only) this.loadReports();
  }

  // --------------------------------------------------------- report charts
  reportCards() {
    return this.shown().filter(c => c.source === "report");
  }
  /** Run the pinned panels (all of them, or the given cards), each on its own: one slow report never holds up the rest. */
  loadReports(cards) {
    const list = cards || this.reportCards();
    if (!list.length) return;
    if (this.state.registry === undefined) {
      this.set({
        registry: null
      });
      safe(repSvc("/registry").getAsync()).then(r => this.set({
        registry: r || null
      }));
    }
    this._repGen = this._repGen || {};
    const patch = {};
    list.forEach(card => {
      const gen = this._repGen[card.id] = (this._repGen[card.id] || 0) + 1;
      const prev = this.state.reports[card.id];
      patch[card.id] = {
        status: "loading",
        title: prev && prev.title
      };
      const done = v => {
        if (this._alive && this._repGen[card.id] === gen) this.setState({
          reports: {
            ...this.state.reports,
            [card.id]: v
          }
        });
      };
      // Like every card: the global date picker and its comparison, never the report's saved dates.
      runReportPanel(card.reportId, card.panelId, this.range(), reportComparison(this.state.comp)).then(run => done({
        status: "ok",
        run,
        title: cleanTitle(((run && run.panels || [])[0] || {}).title) || null
      })).catch(e => done({
        status: "error",
        code: e.code,
        error: e.error,
        title: prev && prev.title
      }));
    });
    this.set({
      reports: {
        ...this.state.reports,
        ...patch
      }
    });
  }
  renderReportCard(card, editing) {
    const st = this.state.reports[card.id];
    const mine = this.state.layout && this.state.layout.source === "mine";
    if (!st || st.status === "loading") return /*#__PURE__*/React.createElement(Card, {
      title: st && st.title
    }, /*#__PURE__*/React.createElement(Pending, {
      h: 200
    }));
    if (st.status === "error") {
      const gone = {
        not_shared: "No longer shared with you",
        panel_removed: "Chart removed from its report",
        report_removed: "Report deleted"
      }[st.code];
      // Someone else's layout (the workspace default) skips a chart this person can't open; their own says why.
      if (gone && !mine && !editing) return null;
      return /*#__PURE__*/React.createElement(Card, {
        title: gone || st.title || "Report chart"
      }, /*#__PURE__*/React.createElement("div", {
        className: "dash-broken"
      }, /*#__PURE__*/React.createElement("span", null, st.error), !editing && (gone ? mine ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "nc-link strong",
        onClick: () => this.removeNow(card.id)
      }, "Remove") : null : /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "nc-link strong",
        onClick: () => this.loadReports([card])
      }, "Try again"))));
    }
    const run = st.run || {};
    const panel = (run.panels || [])[0];
    if (!panel) return /*#__PURE__*/React.createElement(Card, {
      title: st.title
    }, /*#__PURE__*/React.createElement(Empty, null, "Nothing to show for these dates yet."));
    const foot = /*#__PURE__*/React.createElement("div", {
      className: "dash-foot"
    }, "From ", /*#__PURE__*/React.createElement(Link, {
      to: `/Reports/view/${card.reportId}`
    }, run.name || "a report"));
    return /*#__PURE__*/React.createElement(ReportPanel, {
      panel: panel,
      registry: this.state.registry,
      pd: periodOf(run, run.range),
      foot: foot
    });
  }
  /** After cards change: fetch anything a newly shown card needs. */
  fillMissing() {
    const missing = [...this.needs()].filter(g => !(this._loaded || new Set()).has(g));
    if (missing.length) this.load(missing);
    const newCharts = this.reportCards().filter(c => !this.state.reports[c.id]);
    if (newCharts.length) this.loadReports(newCharts);
  }
  async fetchTopProducts(range) {
    const Service = ServiceFactory.getType(`/metrics/product/${range}`);
    const service = new Service();
    const data = await service.paginate({
      baseUrl: "",
      rawUrl: location.href,
      page: 1,
      pageSize: 10
    }).orderByDesc("Views").getAsync();
    return processProductUrls.processStats2(data.items || []);
  }
  dealerWord() {
    const bm = this.state.businessModel;
    return bm === "retail" || bm === "both" ? "customers" : "dealers";
  }

  // ------------------------------------------------------------- what changed
  // Plain-language lines the ribbons do not already carry: the biggest sales mover, the
  // funnel's lead pace, the most-viewed product, the top conversion type. Terse, honest.
  renderChanged() {
    const {
      salesNow,
      funnel,
      products,
      dash
    } = this.state;
    const range = this.range();
    const lines = [];
    if (salesNow && salesNow.totalAmount != null) {
      const movers = [["Revenue", deltaOfPaced(salesNow.totalAmount, salesNow.prevTotalAmount, range)], ["Orders", deltaOfPaced(salesNow.orders, salesNow.prevOrders, range)], [`Buying ${this.dealerWord()}`, deltaOfPaced(salesNow.customers, salesNow.prevCustomers, range)]].filter(([, d]) => d && d.dir !== "flat").sort((a, b) => Math.abs(b[1].pct) - Math.abs(a[1].pct));
      if (movers.length) lines.push({
        tone: movers[0][1].dir === "up" ? "up" : "down",
        text: `${movers[0][0]} is the biggest mover: ${movers[0][1].text}.`
      });
    }
    if (funnel && funnel.length) {
      const lead = funnel.find(f => f.stage === "lead");
      const d = lead ? deltaOfPaced(lead.count, lead.prevCount, range) : null;
      if (lead && d) lines.push({
        tone: d.dir,
        text: `${num(lead.count)} new leads entered the funnel, ${d.text}.`
      });
    }
    if (products && products.names && products.names.length && products.views && products.views.length) {
      lines.push({
        tone: "flat",
        text: `Most-viewed product: ${products.names[0]} (${num(products.views[0])} views).`
      });
    }
    if (dash && dash.conversions) {
      const top = toBarList(dash.conversions, 1)[0];
      if (top) lines.push({
        tone: "flat",
        text: `Top conversion: ${top.name} (${num(top.value)}).`
      });
    }
    if (!lines.length) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-notice dash-changed"
    }, /*#__PURE__*/React.createElement("b", null, "What changed"), lines.slice(0, 3).map((l, i) => /*#__PURE__*/React.createElement("span", {
      key: i,
      className: `line ${l.tone}`
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons"
    }, l.tone === "up" ? "trending_up" : l.tone === "down" ? "trending_down" : "circle"), l.text)));
  }

  // ------------------------------------------------------------------ sales
  renderSalesRibbon() {
    const {
      salesNow,
      salesYtd,
      trend
    } = this.state;
    if (salesNow === null) return null; // no sales feed on this tenant (or the card isn't shown)
    if (salesNow === undefined) return /*#__PURE__*/React.createElement(Ribbon, {
      loading: true,
      tiles: []
    });
    const range = this.range();
    const spark = (trend || []).map(t => Number(t.totalAmount) || 0);
    const tiles = [{
      l: `Revenue · ${rangeLabelFor(range, RANGES)}`,
      v: usd(salesNow.totalAmount),
      d: kitDelta(deltaOfPaced(salesNow.totalAmount, salesNow.prevTotalAmount, range)),
      s: this.state.comp === "prev_year" ? "vs the same dates last year" : "vs the same dates last period",
      sp: spark
    }, {
      l: "Year to date",
      v: salesYtd === undefined ? "…" : usd(salesYtd && salesYtd.totalAmount),
      d: salesYtd ? kitDelta(deltaOf(salesYtd.totalAmount, salesYtd.prevTotalAmount)) : null,
      s: "vs prior-year pace"
    }, {
      l: "Orders",
      v: num(salesNow.orders),
      d: kitDelta(deltaOfPaced(salesNow.orders, salesNow.prevOrders, range)),
      s: Number(salesNow.orders) > 0 ? `${usd(Number(salesNow.totalAmount) / Number(salesNow.orders))} average order` : null
    }, {
      l: `Buying ${this.dealerWord()}`,
      v: num(salesNow.customers),
      d: kitDelta(deltaOfPaced(salesNow.customers, salesNow.prevCustomers, range)),
      s: "placed an order this period"
    }];
    return /*#__PURE__*/React.createElement(Ribbon, {
      tiles: tiles,
      cols: 4,
      comparing: true,
      note: `Sales transactions · pace = the comparison period scaled to the days elapsed · trailing 12 months on the sparkline`
    });
  }

  // --------------------------------------------------------------- activity
  renderActivityRibbon() {
    const {
      dash,
      prev,
      contacts
    } = this.state;
    if (dash === undefined) return /*#__PURE__*/React.createElement(Ribbon, {
      loading: true,
      tiles: []
    });
    if (!dash || !dash.activity) return /*#__PURE__*/React.createElement(Empty, null, "No tracker data for this range \xB7 Manage Data \u203A Tracking");
    const a = dash.activity || {};
    const p = prev && prev.activity || null;
    const rows = dailyRows(dash.performance);
    const spark = k => rows.map(r => r[k]);
    const d = (cur, pv) => p && pv != null ? kitDelta(deltaOf(cur, pv)) : null;
    const known = contacts === undefined ? "…" : contacts && contacts.activeContacts != null ? `${num(contacts.activeContacts)} known contacts active` : "known contacts unavailable";
    const tiles = [{
      l: "Impressions",
      v: num(a.impressions),
      d: d(a.impressions, p && p.impressions),
      s: rows.length ? `${num(Math.round((Number(a.impressions) || 0) / Math.max(1, rows.length)))} per day` : null,
      sp: spark("views")
    }, {
      l: "Visitors",
      v: num(a.users),
      d: d(a.users, p && p.users),
      s: known
    }, {
      l: "Conversions",
      v: num(a.conversions),
      d: d(a.conversions, p && p.conversions),
      s: a.users ? `${((Number(a.conversions) || 0) / Number(a.users) * 100).toFixed(2)}% of visitors` : null,
      sp: spark("conversions")
    }, {
      l: "Product interactions",
      v: num(a.engagements),
      d: d(a.engagements, p && p.engagements),
      s: "configurator, tearsheets, finishes",
      sp: spark("interactions")
    }];
    const comparing = prev === undefined ? undefined : true;
    return /*#__PURE__*/React.createElement(Ribbon, {
      tiles: tiles,
      cols: 4,
      comparing: comparing,
      note: prev === null && this.state.comp !== "prev_period" ? "Activity compares against the previous period only." : null
    });
  }

  // ----------------------------------------------------------------- funnel
  renderFunnel() {
    const {
      funnel
    } = this.state;
    if (funnel === null) return null;
    const range = this.range();
    const byStage = {};
    (funnel || []).forEach(f => {
      byStage[f.stage] = f;
    });
    const maxCount = Math.max(1, ...FUNNEL_META.map(([st]) => (byStage[st] || {}).count || 0));
    return /*#__PURE__*/React.createElement(Card, {
      title: "Funnel",
      hint: "entered this period",
      right: /*#__PURE__*/React.createElement(Link, {
        to: `/Lifecycle/${range}`
      }, "Lifecycle \u2192")
    }, funnel === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 200
    }) : !funnel.length ? /*#__PURE__*/React.createElement(Empty, null, "No lifecycle activity in this range.") : /*#__PURE__*/React.createElement("div", {
      className: "dash-funnel"
    }, FUNNEL_META.map(([stage, label]) => {
      const f = byStage[stage] || {
        count: 0,
        prevCount: 0
      };
      const d = kitDelta(deltaOfPaced(f.count, f.prevCount, range));
      return /*#__PURE__*/React.createElement("div", {
        key: stage,
        className: "stage",
        onClick: this.goto(`/Lifecycle/${range}`),
        title: `Open Lifecycle · ${label}`
      }, /*#__PURE__*/React.createElement("div", {
        className: "top"
      }, /*#__PURE__*/React.createElement("span", {
        className: "l"
      }, label), /*#__PURE__*/React.createElement("span", {
        className: "v"
      }, num(f.count))), /*#__PURE__*/React.createElement("div", {
        className: "bar"
      }, /*#__PURE__*/React.createElement("i", {
        style: {
          width: `${Math.round((f.count || 0) / maxCount * 100)}%`
        }
      })), /*#__PURE__*/React.createElement("div", {
        className: "d"
      }, d ? /*#__PURE__*/React.createElement("span", {
        className: `wa-d ${d.dir}`
      }, d.pct > 0 ? "+" : "", d.pct.toFixed(1), "% vs prev") : /*#__PURE__*/React.createElement("span", {
        className: "wa-d flat"
      }, "no comparison")));
    })));
  }

  // ------------------------------------------------------------------ trend
  renderTrend() {
    const {
      trend
    } = this.state;
    if (trend === null) return null;
    const rows = trend || [];
    return /*#__PURE__*/React.createElement(Card, {
      title: "Revenue trend",
      hint: "trailing 12 months, monthly",
      right: /*#__PURE__*/React.createElement(Link, {
        to: `/Territory/${this.range()}`
      }, "Territory \u2192")
    }, trend === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 240
    }) : rows.length < 2 ? /*#__PURE__*/React.createElement(Empty, null, "Not enough monthly sales history yet.") : /*#__PURE__*/React.createElement(ChartBoundary, null, /*#__PURE__*/React.createElement(Chart, {
      transitions: false,
      style: {
        height: 240
      }
    }, /*#__PURE__*/React.createElement(ChartLegend, {
      visible: false
    }), /*#__PURE__*/React.createElement(ChartCategoryAxis, null, /*#__PURE__*/React.createElement(ChartCategoryAxisItem, {
      categories: rows.map(t => trendLabel(t.period, "month")),
      labels: {
        font: AXIS_FONT,
        color: "#666"
      },
      majorGridLines: {
        visible: false
      }
    })), /*#__PURE__*/React.createElement(ChartValueAxis, null, /*#__PURE__*/React.createElement(ChartValueAxisItem, {
      labels: {
        font: AXIS_FONT,
        color: "#888",
        content: e => usdShort(e.value)
      },
      majorGridLines: {
        color: "#eceff3"
      },
      line: {
        visible: false
      },
      min: 0
    })), /*#__PURE__*/React.createElement(ChartSeries, null, /*#__PURE__*/React.createElement(ChartSeriesItem, {
      type: "column",
      data: rows.map(t => Number(t.totalAmount) || 0),
      name: "Revenue",
      color: "#ff6358",
      gap: 1.4
    })), /*#__PURE__*/React.createElement(ChartTooltip, {
      render: e => e && e.point ? `${e.point.category}: ${usd(e.point.value)}` : ""
    }))));
  }

  // ---------------------------------------------------------------- signals
  renderConversions() {
    const {
      dash
    } = this.state;
    const conv = dash ? toBarList(dash.conversions, 8) : [];
    return /*#__PURE__*/React.createElement(Card, {
      title: "Conversions",
      hint: "by type, this period",
      right: /*#__PURE__*/React.createElement(Link, {
        to: "/Activity/acquisition"
      }, "Website Analytics \u2192")
    }, dash === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 220
    }) : conv.length ? /*#__PURE__*/React.createElement(HBars, {
      rows: conv,
      head: ["Conversion", "", "Count", ""],
      accentFirst: true
    }) : /*#__PURE__*/React.createElement(Empty, null, "No conversions in this period."));
  }
  renderInteractions() {
    const {
      dash
    } = this.state;
    const eng = dash ? toBarList(dash.engagements, 8) : [];
    return /*#__PURE__*/React.createElement(Card, {
      title: "Site interactions",
      hint: "tracked actions by type, this period: search, locator, cart and studio adds",
      right: /*#__PURE__*/React.createElement(Link, {
        to: "/Activity/acquisition"
      }, "Website Analytics \u2192")
    }, dash === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 220
    }) : eng.length ? /*#__PURE__*/React.createElement(HBars, {
      rows: eng,
      head: ["Interaction", "", "Count", ""]
    }) : /*#__PURE__*/React.createElement(Empty, null, "No tracked interactions in this period."));
  }

  // --------------------------------------------------------------- products
  renderProducts() {
    const {
      products
    } = this.state;
    const range = this.range();
    const rows = products && products.views ? products.views.map((v, i) => ({
      name: products.names && products.names[i] || "—",
      value: Number(v) || 0
    })).slice(0, 10) : [];
    return /*#__PURE__*/React.createElement(Card, {
      title: "Top products",
      hint: "views this period",
      right: /*#__PURE__*/React.createElement(Link, {
        to: `/Products/${range}`
      }, "Product Explorer \u2192")
    }, products === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 260
    }) : rows.length ? /*#__PURE__*/React.createElement(HBars, {
      rows: rows,
      head: ["Product", "", "Views", ""],
      accentFirst: true
    }) : /*#__PURE__*/React.createElement(Empty, null, "No product views in this period."));
  }
  // ------------------------------------------------------------ customize
  renderCard(key) {
    switch (key) {
      case "changed":
        return this.renderChanged();
      case "sales":
        return this.renderSalesRibbon();
      case "activity":
        return this.renderActivityRibbon();
      case "funnel":
        return this.renderFunnel();
      case "trend":
        return this.renderTrend();
      case "conversions":
        return this.renderConversions();
      case "interactions":
        return this.renderInteractions();
      case "products":
        return this.renderProducts();
      case "modules":
        return /*#__PURE__*/React.createElement(TenantModules, {
          page: "dashboard",
          range: this.range()
        });
      default:
        return null;
    }
  }
  setDraft(draft) {
    this.setState({
      draft
    }, () => this.fillMissing());
  }
  // Moves act on the full list (hidden cards keep their place relative to the shown ones).
  move(id, dir) {
    const all = this.cards().slice();
    const ctx = this.ctx();
    const vis = all.map((c, i) => available(c, ctx) ? i : -1).filter(i => i >= 0);
    const at = vis.findIndex(i => all[i].id === id);
    const to = at + dir;
    if (at < 0 || to < 0 || to >= vis.length) return;
    const [a, b] = [vis[at], vis[to]];
    [all[a], all[b]] = [all[b], all[a]];
    this.setDraft(all);
  }
  moveTo(fromId, toId) {
    if (!fromId || fromId === toId) return;
    const all = this.cards().slice();
    const from = all.findIndex(c => c.id === fromId);
    if (from < 0) return;
    const [card] = all.splice(from, 1);
    const to = all.findIndex(c => c.id === toId);
    all.splice(to < 0 ? all.length : from <= to ? to + 1 : to, 0, card);
    this.setDraft(all);
  }
  resize(id, size) {
    this.setDraft(this.cards().map(c => c.id === id ? {
      ...c,
      size
    } : c));
  }
  remove(id) {
    this.setDraft(this.cards().filter(c => c.id !== id));
  }
  renderGrid() {
    const editing = !!this.state.draft;
    const ctx = this.ctx();
    const shown = this.shown();
    const cells = [];
    shown.forEach((card, i) => {
      const node = card.source === "report" ? this.renderReportCard(card, editing) : this.renderCard(card.key);
      if (!node && !editing) return;
      const size = sizeOf(card);
      const st = card.source === "report" ? this.state.reports[card.id] : null;
      // Report charts carry their report's name: "Sales" the Plunk card and "Sales · my report" the chart can sit side by side.
      const reportName = st && st.run && st.run.name;
      const label = card.source === "report" ? `${st && st.title || "Report chart"}${reportName ? ` · ${reportName}` : ""}` : (entryOf(card.key) || {}).label;
      const drag = editing ? {
        draggable: true,
        onDragStart: ev => {
          ev.dataTransfer.effectAllowed = "move";
          try {
            ev.dataTransfer.setData("text/plain", card.id);
          } catch (x) {/* IE */}
          this.setState({
            dragFrom: card.id
          });
        },
        onDragOver: ev => {
          ev.preventDefault();
          ev.dataTransfer.dropEffect = "move";
        },
        onDrop: ev => {
          ev.preventDefault();
          this.moveTo(this.state.dragFrom, card.id);
          this.setState({
            dragFrom: null
          });
        },
        onDragEnd: () => this.setState({
          dragFrom: null
        })
      } : {};
      cells.push(/*#__PURE__*/React.createElement("div", _extends({
        key: card.id,
        className: `wa-cell c${size} dash-cell${editing ? " editing" : ""}${this.state.dragFrom === card.id ? " dragging" : ""}`
      }, drag), editing && /*#__PURE__*/React.createElement(CardTools, {
        card: card,
        label: label,
        index: i,
        total: shown.length,
        size: size,
        onSize: s => this.resize(card.id, s),
        onMove: d => this.move(card.id, d),
        onRemove: () => this.remove(card.id)
      }), /*#__PURE__*/React.createElement("div", {
        className: "dash-body"
      }, node || /*#__PURE__*/React.createElement("div", {
        className: "wa-card dash-placeholder"
      }, label, ": nothing to show for this range yet."))));
    });
    if (editing) cells.push(/*#__PURE__*/React.createElement("button", {
      key: "__add",
      type: "button",
      className: "wa-cell c12 dash-drop",
      disabled: this.cards().length >= MAX_CARDS,
      onDragOver: ev => ev.preventDefault(),
      onDrop: ev => {
        ev.preventDefault();
        const all = this.cards().slice();
        const f = all.findIndex(c => c.id === this.state.dragFrom);
        if (f >= 0) {
          all.push(all.splice(f, 1)[0]);
          this.setDraft(all);
        }
        this.setState({
          dragFrom: null
        });
      },
      onClick: () => this.setState({
        adding: true
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "add"), this.cards().length >= MAX_CARDS ? `A Dashboard holds up to ${MAX_CARDS} cards` : "Drop here to move a card to the end, or add a card"));
    if (!cells.length) return /*#__PURE__*/React.createElement(Empty, null, "Your Dashboard has no cards. Choose Customize to add some.");
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid dash-grid"
    }, cells);
  }
  render() {
    const {
      ready,
      error,
      comp,
      dash,
      salesNow,
      layout,
      draft,
      saving,
      toast
    } = this.state;
    if (!ready) return null;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    const range = this.range();
    const editing = !!draft;
    const hasSales = hasTenantFeature("salesTransactions");
    const tracker = dash === undefined ? "…" : dash && dash.activity && Number(dash.activity.impressions) > 0 ? "live" : "no data";
    const chips = editing ? [] : [{
      k: "Tracker",
      v: tracker,
      title: "Impressions recorded in this range"
    }, hasSales && this.needs().has("salesNow") ? {
      k: "Sales feed",
      v: salesNow === undefined ? "…" : salesNow ? "on" : "unavailable",
      title: "Sales transactions feed"
    } : null, {
      k: "Model",
      v: this.state.businessModel === "both" ? "retail + wholesale" : this.state.businessModel
    }];
    const actions = !editing && layout && !layout.provisional ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      onClick: this.customize
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "dashboard_customize"), "Customize") : null;
    return /*#__PURE__*/React.createElement("div", {
      className: `territory-view wa-view dashboard-view${editing ? " dash-editing" : ""}`
    }, /*#__PURE__*/React.createElement(SetupStrip, null), this.state.askPlunkNudge && !editing && /*#__PURE__*/React.createElement("div", {
      className: "wa-notice dash-nudge"
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons"
    }, "auto_awesome"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Meet Ask Plunk"), ": the sparkle in the toolbar answers real questions about your workspace, from day one."), /*#__PURE__*/React.createElement("button", {
      className: "wa-chip",
      onClick: () => {
        PubSub.publish('plunk-assistant-toggle', {});
        this.dismissNudge();
      }
    }, "Try it"), /*#__PURE__*/React.createElement("button", {
      className: "wa-chip",
      onClick: () => this.dismissNudge()
    }, "Dismiss")), /*#__PURE__*/React.createElement(PageHeader, {
      title: "Dashboard",
      subtitle: `${rangeLabelFor(range, RANGES)} · vs ${compLabel(comp).toLowerCase()} · ${layout && layout.source === "mine" ? "your layout" : layout && layout.source === "default" ? "the workspace layout" : "sales, funnel and site activity at a glance"}`,
      chips: chips,
      actions: actions
    }), layout && layout.defaultChanged && !editing && /*#__PURE__*/React.createElement("div", {
      className: "wa-notice dash-nudge"
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons"
    }, "dashboard_customize"), /*#__PURE__*/React.createElement("span", null, "The workspace Dashboard changed. You're seeing your own layout."), /*#__PURE__*/React.createElement("button", {
      className: "wa-chip",
      onClick: this.useNewDefault
    }, "Use the new default"), /*#__PURE__*/React.createElement("button", {
      className: "wa-chip",
      onClick: this.keepMine
    }, "Keep mine")), editing && /*#__PURE__*/React.createElement(EditBar, {
      count: draft.length,
      busy: saving,
      canSetDefault: !!(layout && layout.canSetDefault),
      hasDefault: !!(layout && (layout.hasDefault || layout.source === "default")),
      onClearDefault: this.clearDefault,
      canReset: layout && layout.source === "mine",
      resetLabel: layout && layout.hasDefault ? "Reset to the workspace default" : "Reset to Plunk's default",
      onAdd: () => this.setState({
        adding: true
      }),
      onReset: this.reset,
      onSaveDefault: this.askDefault,
      onCancel: this.cancel,
      onDone: this.done
    }), layout ? this.renderGrid() : /*#__PURE__*/React.createElement(Pending, {
      h: 240
    }), this.state.adding && editing && /*#__PURE__*/React.createElement(AddCardSheet, {
      cards: draft,
      ctx: this.ctx(),
      onAdd: this.add,
      onAddReport: this.addReport,
      onNewChart: this.newChart,
      onClose: () => this.setState({
        adding: false
      })
    }), this.state.askDefault && /*#__PURE__*/React.createElement(DefaultConfirm, {
      customized: this.state.askDefault.customized,
      busy: saving,
      onSave: this.saveDefault,
      onClose: () => this.setState({
        askDefault: null
      })
    }), toast && /*#__PURE__*/React.createElement(Toast, {
      text: toast.text,
      action: toast.action,
      onAction: this.toastAction,
      onClose: () => this.setState({
        toast: null
      })
    }));
  }
}
const RoutableDashboardComponent = withRouter(DashboardComponent);

// Route-level wrapper kept for index.jsx compatibility ({DashboardViewComponent}).
export class DashboardViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(RoutableDashboardComponent, {
      params: this.props.params
    });
  }
}
export default {
  DashboardViewComponent
};